import { useEffect, useRef, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { safeGradient, safeRadius, safeAlpha } from '../utils/canvasSafe';

// Cognitive state palette + motion metadata (from V2 constants)
const COL = {
  standby:   { r: 0,   g: 200, b: 212 },
  listening: { r: 0,   g: 229, b: 170 },
  thinking:  { r: 255, g: 140, b: 0   },
  speaking:  { r: 52,  g: 211, b: 153 },
};

const META = {
  standby:   { speed: 0.85, scale: 1.00, pr: 1.1 },
  listening: { speed: 1.85, scale: 1.12, pr: 2.0 },
  thinking:  { speed: 2.80, scale: 0.87, pr: 3.1 },
  speaking:  { speed: 0.95, scale: 1.07, pr: 1.3 },
};

function normalizeVoiceLevel(value) {
  const raw = Number(value);
  if (!Number.isFinite(raw)) return 0;
  if (raw >= 0 && raw <= 1) return raw;
  if (raw > 1 && raw <= 20) return Math.max(0, Math.min(1, raw / 12));
  return Math.max(0, Math.min(1, raw / 100));
}

export function NeuralOrb({ isListening = false, isSpeaking = false, isProcessing = false, onTouchActivate, continuousMode = false, voiceLevel = 0 }) {
  const canvasRef           = useRef(null);
  const animRef             = useRef(null);
  const cogStateRef         = useRef('standby');
  const interactingRef      = useRef(false);
  const continuousModeRef   = useRef(false);
  const voiceLevelRef       = useRef(0);
  const [isTouching, setIsTouching]       = useState(false);
  const [touchProgress, setTouchProgress] = useState(0);
  const touchTimerRef       = useRef(null);
  const touchStartRef       = useRef(null);
  const progressIntervalRef = useRef(null);

  // Translate boolean props → cogState string
  const cogState = isSpeaking ? 'speaking' : isListening ? 'listening' : isProcessing ? 'thinking' : 'standby';

  useEffect(() => { cogStateRef.current       = cogState;       }, [cogState]);
  useEffect(() => { interactingRef.current    = isTouching;     }, [isTouching]);
  useEffect(() => { continuousModeRef.current = continuousMode; }, [continuousMode]);
  useEffect(() => { voiceLevelRef.current     = normalizeVoiceLevel(voiceLevel); }, [voiceLevel]);

  // Touch-to-talk: microphone turns on immediately while holding
  const handleTouchStart = useCallback((e) => {
    e.preventDefault();
    setIsTouching(true);
    setTouchProgress(1);
    if (touchTimerRef.current)       { clearTimeout(touchTimerRef.current);        touchTimerRef.current = null; }
    if (progressIntervalRef.current) { clearInterval(progressIntervalRef.current); progressIntervalRef.current = null; }
    if (onTouchActivate) onTouchActivate('hold-start');
  }, [onTouchActivate]);

  const handleTouchEnd = useCallback(() => {
    setIsTouching(false);
    setTouchProgress(0);
    if (touchTimerRef.current)       { clearTimeout(touchTimerRef.current);        touchTimerRef.current = null; }
    if (progressIntervalRef.current) { clearInterval(progressIntervalRef.current); progressIntervalRef.current = null; }
    if (onTouchActivate) onTouchActivate('hold-end');
  }, [onTouchActivate]);

  // === NEURAL MESH CANVAS ===
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const DPR  = Math.min(window.devicePixelRatio || 1, 2);
    const S    = 320, cx = 160, cy = 160, ORB_R = 102;
    canvas.width  = S * DPR;
    canvas.height = S * DPR;
    const ctx = canvas.getContext('2d');
    ctx.scale(DPR, DPR);

    // 120 nodes distributed on a 3D sphere (reduced from 195 — O(n²) perf)
    const nodes = Array.from({ length: 120 }, () => {
      const θ = Math.random() * Math.PI * 2;
      const φ = Math.acos(2 * Math.random() - 1);
      const r = 6 + Math.random() * (ORB_R - 6);
      const a = Math.random() * Math.PI * 2;
      const s = 0.18 + Math.random() * 0.60;
      return {
        x:  cx + r * Math.sin(φ) * Math.cos(θ),
        y:  cy + r * Math.sin(φ) * Math.sin(θ),
        vx: Math.cos(a) * s,
        vy: Math.sin(a) * s,
        ph: Math.random() * Math.PI * 2,
        fr: 0.34 + Math.random() * 0.74,
        wb: 0.26 + Math.random() * 0.90,
        sz: 0.55 + Math.random() * 1.80,
        rx: 0, ry: 0,
      };
    });

    // Start at standby color
    let cR = COL.standby.r, cG = COL.standby.g, cB = COL.standby.b;
    let interScale = 1.0;
    let displayedVoiceLevel = 0;
    let frameCount = 0;

    function tick(ts) {
      // Pause RAF when browser tab is hidden — saves CPU/battery
      if (document.hidden) { animRef.current = null; return; }

      frameCount++;
      const cs = cogStateRef.current;

      // Standby throttle: skip every other frame (~30fps) when idle
      if (cs === 'standby' && frameCount % 2 === 0) {
        animRef.current = requestAnimationFrame(tick);
        return;
      }

      try {
        const t  = ts * 0.001;
        const c  = COL[cs];
        const m  = META[cs];

        // Smooth color interpolation — faster for thinking state
        const lp = cs === 'thinking' ? 0.052 : 0.020;
        cR += (c.r - cR) * lp;
        cG += (c.g - cG) * lp;
        cB += (c.b - cB) * lp;
        const R = Math.round(cR), G = Math.round(cG), B = Math.round(cB);

        // Breath scale — whole-sphere oscillation, frequency varies by state
        const breathSpeed = cs === 'thinking' ? 1.8 : cs === 'speaking' ? 1.5 : cs === 'listening' ? 0.7 : 0.6;
        const breathScale = 1 + Math.sin(t * breathSpeed) * 0.04;

        // Heartbeat pattern ~67 BPM (systole + diastole) — only when continuousMode active
        const hbCycle = t % 0.9;
        const hbRaw   = continuousModeRef.current
          ? Math.exp(-Math.pow(hbCycle - 0.10, 2) * 80) + Math.exp(-Math.pow(hbCycle - 0.30, 2) * 80) * 0.6
          : 0;
        const hbBoost    = Math.min(hbRaw, 1.0);
        const totalScale = breathScale * (1 + hbBoost * 0.06);

        // 15% tint toward #00ffcc (wake) when continuousMode — subtle "attentive" warmth
        const mix = continuousModeRef.current ? 0.15 : 0;
        let mR  = Math.round(R * (1 - mix));                // wake r=0
        let mG  = Math.round(G * (1 - mix) + 255 * mix);
        let mB  = Math.round(B * (1 - mix) + 204 * mix);

        const spd   = m.speed;
        const sc    = m.scale;
        // `onRmsChanged` do reconhecedor é a única fonte de energia durante
        // a captura. Suavizamos seus frames reais para evitar saltos visuais,
        // mas não inventamos uma onda quando o ambiente está silencioso ou o
        // serviço Android não fornece RMS.
        const liveLevel = cs === 'listening' ? voiceLevelRef.current : 0;
        const smoothing = liveLevel > displayedVoiceLevel ? 0.24 : 0.075;
        displayedVoiceLevel += (liveLevel - displayedVoiceLevel) * smoothing;
        if (Math.abs(displayedVoiceLevel) < 0.002) displayedVoiceLevel = 0;
        const signalEnergy = displayedVoiceLevel;
        // A voz real também muda discretamente o tom do núcleo. Em silêncio
        // ele fica ciano; conforme a fala sobe, caminha para violeta. Isso
        // compartilha a mesma leitura cromática da barra de reconhecimento.
        if (cs === 'listening' && liveLevel > 0.02) {
          const hueMix = Math.min(0.36, 0.08 + liveLevel * 0.28);
          mR = Math.round(mR * (1 - hueMix) + 139 * hueMix);
          mG = Math.round(mG * (1 - hueMix) + 92 * hueMix);
          mB = Math.round(mB * (1 - hueMix) + 246 * hueMix);
        }
        const pulse = Math.sin(t * m.pr) * (6 + signalEnergy * 9);

        const targetScale = interactingRef.current ? 1.07 : 1.0;
        interScale += (targetScale - interScale) * 0.038;

        ctx.clearRect(0, 0, S, S);

        // Ambient outer glow
        const g1 = ctx.createRadialGradient(cx, cy, 0, cx, cy, 134 + pulse * 0.3);
        g1.addColorStop(0,    `rgba(${mR},${mG},${mB},0.12)`);
        g1.addColorStop(0.44, `rgba(${mR},${mG},${mB},0.04)`);
        g1.addColorStop(1,    'rgba(0,0,0,0)');
        ctx.beginPath(); ctx.arc(cx, cy, 134 + pulse * 0.3, 0, Math.PI * 2);
        ctx.fillStyle = g1; ctx.fill();

        // Inner core glow
        const g2 = ctx.createRadialGradient(cx, cy, 0, cx, cy, 46);
        g2.addColorStop(0,    `rgba(${mR},${mG},${mB},0.24)`);
        g2.addColorStop(0.68, `rgba(${mR},${mG},${mB},0.06)`);
        g2.addColorStop(1,    'rgba(0,0,0,0)');
        ctx.beginPath(); ctx.arc(cx, cy, 46, 0, Math.PI * 2);
        ctx.fillStyle = g2; ctx.fill();

        // Volumetric mesh glow — envelope at sphere level, drawn before nodes
        const glowR = safeRadius(ORB_R * sc * interScale * totalScale + 24, 10);
        const glowA = safeAlpha(0.28 + hbBoost * 0.10 + signalEnergy * 0.10);
        const gVol  = safeGradient(ctx, cx, cy, 0, cx, cy, glowR);
        gVol.addColorStop(0,    `rgba(${mR},${mG},${mB},${glowA})`);
        gVol.addColorStop(0.50, `rgba(${mR},${mG},${mB},${safeAlpha(glowA * 0.35)})`);
        gVol.addColorStop(1,    'rgba(0,0,0,0)');
        ctx.beginPath(); ctx.arc(cx, cy, glowR, 0, Math.PI * 2);
        ctx.fillStyle = gVol; ctx.fill();

        // Pulse ring — only during active listening or speaking
        if (cs === 'listening' || cs === 'speaking') {
          const pr = 78 + Math.sin(t * 3.8) * (16 + signalEnergy * 12);
          const pa = 0.16 + Math.abs(Math.sin(t * 3.8)) * 0.14 + signalEnergy * 0.12;
          ctx.beginPath(); ctx.arc(cx, cy, pr, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(${mR},${mG},${mB},${pa})`;
          ctx.lineWidth = 1.4; ctx.stroke();
        }

        // Move nodes — elastic sphere boundary (breathes with totalScale)
        const effR = ORB_R * sc * interScale * totalScale;
        nodes.forEach(n => {
          n.x += n.vx * spd;
          n.y += n.vy * spd;
          const dx = n.x - cx, dy = n.y - cy;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist > effR) {
            const nx = dx / dist, ny = dy / dist;
            const dot = n.vx * nx + n.vy * ny;
            n.vx -= 2 * dot * nx;
            n.vy -= 2 * dot * ny;
            n.x = cx + nx * (effR - 0.5);
            n.y = cy + ny * (effR - 0.5);
          }
          // Rendered position = base + breathing wobble
          n.rx = n.x + Math.cos(t * n.fr + n.ph)       * n.wb * spd;
          n.ry = n.y + Math.sin(t * n.fr * 0.8 + n.ph) * n.wb * spd;
        });

        // Filament pulse — opacity oscillates by cognitive state
        const baseConnAlpha = cs === 'speaking' ? 0.60 : cs === 'thinking' ? 0.48 : 0.37;
        const filPulse = cs === 'speaking'
          ? 0.50 + Math.abs(Math.sin(t * 4.2)) * 0.50                              // fast + intense
          : cs === 'thinking'
            ? 0.50 + Math.abs(Math.sin(t * 2.8 + Math.sin(t * 5.0) * 0.4)) * 0.50 // irregular
            : 0.70 + Math.sin(t * 0.9) * 0.30;                                     // slow + soft

        // Neural connections — filaments between nearby nodes
        for (let i = 0; i < nodes.length; i++) {
          for (let j = i + 1; j < nodes.length; j++) {
            const ex = nodes[i].rx - nodes[j].rx;
            const ey = nodes[i].ry - nodes[j].ry;
            const ed = Math.sqrt(ex * ex + ey * ey);
            if (ed < 48) {
              ctx.beginPath();
              ctx.moveTo(nodes[i].rx, nodes[i].ry);
              ctx.lineTo(nodes[j].rx, nodes[j].ry);
              ctx.strokeStyle = `rgba(${mR},${mG},${mB},${safeAlpha((1 - ed / 48) * baseConnAlpha * filPulse)})`;
              ctx.lineWidth = 0.44; ctx.stroke();
            }
          }
        }

        // Node dots + soft halo
        nodes.forEach(n => {
          const br = 0.28 + Math.sin(t * n.fr + n.ph) * 0.72;
          ctx.beginPath(); ctx.arc(n.rx, n.ry, n.sz, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${mR},${mG},${mB},${br})`; ctx.fill();
          ctx.beginPath(); ctx.arc(n.rx, n.ry, n.sz * 3.4, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${mR},${mG},${mB},${br * 0.065})`; ctx.fill();
        });

        // Orbital rings (horizontal + vertical)
        const isThinking = cs === 'thinking';
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(t * (isThinking ? 1.2 : 0.40));
        ctx.scale(1, 0.20);
        ctx.beginPath(); ctx.arc(0, 0, 114 + pulse * 0.14, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(${mR},${mG},${mB},0.28)`; ctx.lineWidth = 0.78; ctx.stroke();
        ctx.restore();

        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(-t * (isThinking ? 0.75 : 0.24) + 1.1);
        ctx.scale(0.16, 1);
        ctx.beginPath(); ctx.arc(0, 0, 114 + pulse * 0.10, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(${mR},${mG},${mB},0.20)`; ctx.lineWidth = 0.62; ctx.stroke();
        ctx.restore();
      } catch {}

      animRef.current = requestAnimationFrame(tick);
    }

    animRef.current = requestAnimationFrame(tick);

    // Restart animation when tab becomes visible again
    const handleVisibility = () => {
      if (!document.hidden && !animRef.current) {
        frameCount = 0;
        animRef.current = requestAnimationFrame(tick);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      cancelAnimationFrame(animRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  // CSS drop-shadow reacts to cogState; CSS transition handles smoothing
  const c   = COL[cogState];
  const signalLevel = isListening ? normalizeVoiceLevel(voiceLevel) : 0;
  const hot = isTouching || isListening || isProcessing || isSpeaking;
  const g1  = hot ? 0.52 + signalLevel * 0.12 : 0.30;
  const g2  = hot ? 0.22 + signalLevel * 0.08 : 0.14;
  const r1  = hot ? 46 + signalLevel * 9 : 34;
  const r2  = hot ? 108 + signalLevel * 17 : 86;

  return (
    <motion.div
      className="relative flex items-center justify-center cursor-pointer select-none"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: isTouching ? 0.97 : 1 }}
      transition={{ duration: isTouching ? 0.2 : 1.2, ease: 'easeOut' }}
      data-testid="neural-orb"
      onMouseDown={handleTouchStart}
      onMouseUp={handleTouchEnd}
      onMouseLeave={handleTouchEnd}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <canvas
        ref={canvasRef}
        className="w-[320px] h-[320px] md:w-[380px] md:h-[380px]"
        style={{
          display: 'block',
          borderRadius: '50%',
          outline: 'none',
          filter: `drop-shadow(0 0 ${r1}px rgba(${c.r},${c.g},${c.b},${g1})) drop-shadow(0 0 ${r2}px rgba(${c.r},${c.g},${c.b},${g2}))`,
          transition: 'filter 1.0s ease',
        }}
      />
      {/* Touch progress ring */}
      {isTouching && touchProgress > 0 && (
        <svg className="absolute w-[200px] h-[200px] md:w-[240px] md:h-[240px] pointer-events-none" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="45" fill="none" stroke="rgba(60,200,255,0.1)" strokeWidth="1" />
          <circle cx="50" cy="50" r="45" fill="none" stroke="rgba(60,200,255,0.5)" strokeWidth="1.5"
            strokeDasharray={`${touchProgress * 283} 283`}
            strokeLinecap="round"
            transform="rotate(-90 50 50)"
            style={{ transition: 'stroke-dasharray 0.05s linear' }}
          />
        </svg>
      )}
    </motion.div>
  );
}
